import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail, avatarSrc } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { MagnifyingGlass, Lock, ArrowRight, UserPlus, Check, X, Clock } from "@phosphor-icons/react";

export default function NetworkPage() {
  const [agents, setAgents] = useState([]);
  const [pending, setPending] = useState([]);
  const [friends, setFriends] = useState([]);
  const [filters, setFilters] = useState({ q: "", area: "", specialty: "all", min_properties: "", min_success_rate: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.area) params.area = filters.area;
      if (filters.specialty && filters.specialty !== "all") params.specialty = filters.specialty;
      if (filters.min_properties) params.min_properties = filters.min_properties;
      if (filters.min_success_rate) params.min_success_rate = filters.min_success_rate;
      const [a, p, f] = await Promise.all([
        api.get("/agents", { params }),
        api.get("/friends/pending"),
        api.get("/friends"),
      ]);
      setAgents(a.data);
      setPending(p.data);
      setFriends(f.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filters]); // eslint-disable-line

  const connect = async (id) => {
    try {
      await api.post(`/friends/request/${id}`);
      toast.success("Request sent");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const respond = async (reqId, action) => {
    await api.post(`/friends/respond/${reqId}?action=${action}`);
    toast.success(action === "accept" ? "Connected" : "Rejected");
    load();
  };

  return (
    <div className="min-h-screen p-6 md:p-10 pb-24" data-testid="network-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">The Network</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight">Agents in your orbit.</h1>
        <p className="text-zinc-500 mt-2 max-w-xl">
          Browse agents by area, inventory, specialty, and closing rate. Send a request to unlock full profiles.
        </p>
      </div>

      {/* Pending requests */}
      {pending.length > 0 && (
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3 flex items-center gap-2">
            <Clock size={14} /> Pending invitations ({pending.length})
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="pending-requests">
            {pending.map((p) => (
              <div key={p.request_id} className="card-surface rounded-xl p-4 flex items-center gap-3">
                <Avatar className="h-12 w-12 border border-white/10">
                  <AvatarImage src={avatarSrc(p.from.avatar_url)} />
                  <AvatarFallback className="bg-ink-700">{p.from.name[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{p.from.name}</div>
                  <div className="text-xs text-zinc-500 truncate">
                    {p.from.area} · {p.from.specialty}
                  </div>
                </div>
                <button
                  onClick={() => respond(p.request_id, "accept")}
                  data-testid={`accept-${p.request_id}`}
                  className="p-2 rounded-md bg-gold text-ink-900 hover:bg-gold-light transition-colors"
                  title="Accept"
                >
                  <Check size={14} weight="bold" />
                </button>
                <button
                  onClick={() => respond(p.request_id, "reject")}
                  data-testid={`reject-${p.request_id}`}
                  className="p-2 rounded-md border hairline text-zinc-400 hover:text-red-400 transition-colors"
                  title="Reject"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card-surface rounded-xl p-4 grid grid-cols-1 md:grid-cols-5 gap-3 mb-6" data-testid="network-filters">
        <div className="relative md:col-span-2">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            placeholder="Search by name or area"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="pl-9 bg-ink-700 border-white/10"
            data-testid="network-search"
          />
        </div>
        <Input
          placeholder="City or Area (Mumbai, Bandra...)"
          value={filters.area}
          onChange={(e) => setFilters({ ...filters, area: e.target.value })}
          className="bg-ink-700 border-white/10"
          data-testid="network-area"
        />
        <Select value={filters.specialty} onValueChange={(v) => setFilters({ ...filters, specialty: v })}>
          <SelectTrigger className="bg-ink-700 border-white/10" data-testid="network-specialty">
            <SelectValue placeholder="Specialty" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All specialties</SelectItem>
            <SelectItem value="Residential">Residential</SelectItem>
            <SelectItem value="Luxury">Luxury</SelectItem>
            <SelectItem value="Commercial">Commercial</SelectItem>
          </SelectContent>
        </Select>
        <div className="grid grid-cols-2 gap-2">
          <Input
            placeholder="Min props"
            type="number"
            value={filters.min_properties}
            onChange={(e) => setFilters({ ...filters, min_properties: e.target.value })}
            className="bg-ink-700 border-white/10 font-mono"
            data-testid="network-min-properties"
          />
          <Input
            placeholder="Min rate %"
            type="number"
            value={filters.min_success_rate}
            onChange={(e) => setFilters({ ...filters, min_success_rate: e.target.value })}
            className="bg-ink-700 border-white/10 font-mono"
            data-testid="network-min-rate"
          />
        </div>
      </div>

      {/* Agent grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger" data-testid="agents-grid">
        {loading && agents.length === 0 ? (
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="card-surface rounded-xl p-5" data-testid={`agent-skeleton-${i}`}>
              <div className="flex items-center gap-3">
                <Skeleton className="h-12 w-12 rounded-full bg-white/[0.08]" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4 bg-white/[0.08]" />
                  <Skeleton className="h-3 w-1/2 bg-white/[0.05]" />
                </div>
              </div>
              <Skeleton className="h-3 w-full mt-4 bg-white/[0.05]" />
              <Skeleton className="h-3 w-5/6 mt-2 bg-white/[0.05]" />
              <Skeleton className="h-8 w-full mt-4 bg-white/[0.06]" />
            </div>
          ))
        ) : (
          <>
            {agents.map((a) => (
              <AgentCard key={a.id} agent={a} onConnect={() => connect(a.id)} />
            ))}
            {!loading && agents.length === 0 && (
              <div className="col-span-full card-surface rounded-xl p-10 text-center text-zinc-500">
                No agents match your filters.
              </div>
            )}
          </>
        )}
      </div>

      {/* Friends */}
      {friends.length > 0 && (
        <div className="mt-12">
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3">Your connections</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="friends-list">
            {friends.map((f) => (
              <Link
                key={f.id}
                to={`/agents/${f.id}`}
                className="card-surface rounded-xl p-4 flex items-center gap-3 hover:-translate-y-[2px] transition-all"
                data-testid={`friend-card-${f.id}`}
              >
                <Avatar className="h-11 w-11 border border-white/10">
                  <AvatarImage src={avatarSrc(f.avatar_url)} />
                  <AvatarFallback className="bg-ink-700">{f.name[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{f.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{f.area}{f.city ? `, ${f.city}` : ""}</div>
                </div>
                <ArrowRight size={14} className="text-zinc-500" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AgentCard({ agent, onConnect }) {
  const isFriend = agent.friendship === "friends";
  const isOutgoing = agent.friendship === "outgoing";
  const isIncoming = agent.friendship === "incoming";
  return (
    <div className="card-surface rounded-xl p-5 flex flex-col gap-4 relative group transition-all duration-300 hover:-translate-y-[2px]" data-testid={`agent-card-${agent.id}`}>
      <Link to={`/agents/${agent.id}`} className="flex items-start gap-3 hover:text-gold transition-colors" data-testid={`agent-card-link-${agent.id}`}>
        <Avatar className={`h-14 w-14 border ${isFriend ? "border-gold/40" : "border-white/10"}`}>
          <AvatarImage src={avatarSrc(agent.avatar_url)} />
          <AvatarFallback className="bg-ink-700">{agent.name[0]}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{agent.name}</div>
          <div className="text-xs text-zinc-500">{agent.specialty} · {agent.area}{agent.city ? `, ${agent.city}` : ""}</div>
        </div>
      </Link>

      <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t hairline">
        <Stat label="Properties" value={agent.properties_count} />
        <Stat label="Closed" value={agent.sold_count} />
        <Stat label="Rate" value={`${agent.success_rate}%`} locked={!isFriend} />
      </div>

      <div className="flex gap-2">
        <Link
          to={`/agents/${agent.id}`}
          className="flex-1 text-center text-xs uppercase tracking-[0.2em] py-2.5 rounded-md border hairline hover:border-gold hover:text-gold transition-all"
          data-testid={`view-agent-${agent.id}`}
        >
          View
        </Link>
        {isFriend ? (
          <div className="flex-1 text-center text-xs uppercase tracking-[0.2em] py-2.5 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center justify-center gap-1.5">
            <Check size={12} weight="bold" /> Connected
          </div>
        ) : isOutgoing ? (
          <div className="flex-1 text-center text-xs uppercase tracking-[0.2em] py-2.5 rounded-md border hairline text-zinc-500 flex items-center justify-center gap-1.5">
            <Clock size={12} /> Pending
          </div>
        ) : isIncoming ? (
          <div className="flex-1 text-center text-xs uppercase tracking-[0.2em] py-2.5 rounded-md border hairline text-amber-400 flex items-center justify-center gap-1.5">
            Respond below
          </div>
        ) : (
          <button
            onClick={onConnect}
            data-testid={`connect-${agent.id}`}
            className="flex-1 text-xs uppercase tracking-[0.2em] py-2.5 rounded-md bg-gold text-ink-900 hover:bg-gold-light transition-all flex items-center justify-center gap-1.5"
          >
            <UserPlus size={12} weight="bold" /> Connect
          </button>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, locked }) {
  return (
    <div className="relative">
      <div className={`font-mono text-lg ${locked ? "blur-[5px] select-none" : "text-gold"}`}>{value}</div>
      {locked && (
        <Lock size={12} className="absolute right-1 top-1 text-zinc-500" />
      )}
      <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}
